import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import apiClient, { productsAPI, discountsAPI, manageAPI, proxyImageUrl, cheeseAPI, adminItemsAPI } from '../api/client';
import { useAuthStore } from '../store';
import { CropModal } from './CropModal';
import '../styles/product-modal.css';

const SWIPE_THRESHOLD = 50;

const CopyButton = ({ onCopy, className = '' }) => {
  const [state, setState] = useState('idle');
  const handle = (e) => {
    e.stopPropagation();
    setState('loading');
    onCopy()
      .then(() => { setState('done'); setTimeout(() => setState('idle'), 1500); })
      .catch(err => { console.error('Copy failed:', err); setState('error'); setTimeout(() => setState('idle'), 2000); });
  };
  return (
    <button
      className={`copy-btn${state === 'done' ? ' copy-btn--done' : ''}${state === 'error' ? ' copy-btn--error' : ''}${className ? ` ${className}` : ''}`}
      onClick={handle}
      disabled={state === 'loading'}
      title={state === 'done' ? 'Скопировано!' : state === 'error' ? 'Ошибка' : 'Копировать'}
    >
      {state === 'loading'
        ? <span className="copy-spinner" />
        : state === 'done'
        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
        : state === 'error'
        ? '✗'
        : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>}
    </button>
  );
};

const copyImageToClipboard = (photoUrl) => {
  const buildPng = async () => {
    const blob = photoUrl.includes('alldrop.ink/images/')
      ? await fetch(photoUrl).then(r => r.blob())
      : await apiClient.get(`/products/proxy-image?url=${encodeURIComponent(photoUrl)}`, { responseType: 'blob' }).then(r => r.data);
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    canvas.getContext('2d').drawImage(bmp, 0, 0);
    return new Promise((resolve, reject) =>
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png')
    );
  };
  return navigator.clipboard.write([new ClipboardItem({ 'image/png': buildPng() })]);
};

const SupplierLink = ({ href, label }) => {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="supplier-link-btn"
    >
      {label}
    </a>
  );
};

const formatPrice = (raw) => {
  if (!raw) return null;
  const num = parseFloat(String(raw).replace(/\s/g, '').replace(',', '.'));
  if (isNaN(num)) return raw;
  return Number.isInteger(num)
    ? num.toLocaleString('ru-RU')
    : num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const filterGrid = (grid, sizesStr) => {
  if (!grid || !sizesStr) return grid;
  const available = new Set(sizesStr.split(' ').map(p => p.split(':')[0]));
  const filtered = grid.split(' | ').filter(entry => available.has(entry.trim().split(' ')[0]));
  return filtered.length ? filtered.join(' | ') : null;
};

const cyclePriceSort = (cur) => {
  if (cur === 'price_asc')  return 'price_desc';
  if (cur === 'price_desc') return 'default';
  return 'price_asc';
};

const SkuValue = ({ sku }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(sku).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 600);
    }).catch(() => {});
  };
  return (
    <span className={`detail-value sku-value${copied ? ' sku-value--copied' : ''}`} onClick={handle}>
      {sku}
      <span className="sku-copied-hint">{copied ? 'Скопировано!' : 'Копировать'}</span>
    </span>
  );
};

// Убираем эмодзи из строки для сопоставления с нормализованными именами поставщиков
const EMOJI_RE = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F9FF}\u{1F1E0}-\u{1F1FF}\u{2702}-\u{27B0}\u{24C2}-\u{1F251}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2300}-\u{23FF}]+/gu;
const stripEmoji = (s) => s ? s.replace(EMOJI_RE, '').trim() : s;

const SuppliersSection = ({ productId, productSku, product, onZoomChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discountMap, setDiscountMap] = useState({});
  const isAdmin = useAuthStore((state) => state.user?.is_admin);
  const [sortBy, setSortBy] = useState('default');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('alldrop_suppliersView') || 'cards'; } catch { return 'cards'; }
  });
  const toggleViewMode = () => {
    setViewMode(m => {
      const next = m === 'cards' ? 'table' : 'cards';
      try { localStorage.setItem('alldrop_suppliersView', next); } catch {}
      return next;
    });
  };
  const [tableSort, setTableSort] = useState({ key: 'price', dir: 'desc' });
  const handleTableSort = (key) => {
    setTableSort(cur => cur.key === key
      ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'supplier' ? 'asc' : 'desc' });
  };
  const [zoomIdx, setZoomIdx] = useState(null);
  const lightboxTouchX = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editResult, setEditResult] = useState(null);
  const [editError, setEditError] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoUploadRef = useRef(null);

  const [rotatingUrl, setRotatingUrl] = useState(null);
  const [photoTimestamps, setPhotoTimestamps] = useState(() => {
    try {
      const raw = localStorage.getItem('alldrop_photoTs');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return Object.fromEntries(Object.entries(parsed).filter(([, ts]) => ts > cutoff));
    } catch { return {}; }
  });
  const _OUR_ORIGIN = 'https://alldrop.ink/images';
  const isOurImage = (url) => typeof url === 'string' && url.startsWith(_OUR_ORIGIN);
  const withBust = (url) => {
    if (!url) return url;
    const base = url.split('?')[0];
    const ts = photoTimestamps[base];
    return ts ? `${base}?t=${ts}` : url;
  };
  const handleRotate = async (url, degrees) => {
    if (!url || rotatingUrl) return;
    const base = url.split('?')[0];
    setRotatingUrl(base);
    try {
      await adminItemsAPI.rotatePhoto(base, degrees);
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (err) {
      alert(`Ошибка поворота: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };

  const handleRevert = async (url) => {
    if (!url || rotatingUrl) return;
    const base = url.split('?')[0];
    setRotatingUrl(base);
    try {
      await adminItemsAPI.revertPhoto(base);
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (err) {
      alert(`Ошибка восстановления: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setEditFields({
      catalog_id: productSku || '',
      photo_url: s.supplier_photo_url || '',
      photo_link: s.photo_link || '',
    });
    setEditResult(null);
    setEditError('');
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditFields({});
    setEditResult(null);
    setEditError('');
  };

  const setEditField = (key, val) => {
    setEditFields(f => ({ ...f, [key]: val }));
    setEditError('');
    setEditResult(null);
  };

  const uploadPhotoFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setPhotoUploading(true);
    try {
      const res = await adminItemsAPI.uploadPhoto(file);
      setEditField('photo_url', res.data.url);
    } catch (err) {
      setEditError(err?.response?.data?.detail || 'Ошибка загрузки фото');
    } finally {
      setPhotoUploading(false);
      if (photoUploadRef.current) photoUploadRef.current.value = '';
    }
  };

  const handlePhotoFileInput = (e) => uploadPhotoFile(e.target.files?.[0]);

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    uploadPhotoFile(e.dataTransfer.files?.[0]);
  };

  const handlePhotoPaste = (e) => {
    const item = Array.from(e.clipboardData.items).find(i => i.type.startsWith('image/'));
    if (item) { e.preventDefault(); uploadPhotoFile(item.getAsFile()); }
  };

  const handleSaveEdit = async (s) => {
    setEditLoading(true);
    setEditError('');
    setEditResult(null);
    const changed = {};
    if (editFields.catalog_id.trim() && editFields.catalog_id.trim() !== productSku)
      changed.catalog_id = editFields.catalog_id.trim();
    if (editFields.photo_url.trim() !== (s.supplier_photo_url || ''))
      changed.photo_url = editFields.photo_url.trim();
    if (editFields.photo_link.trim() !== (s.photo_link || ''))
      changed.photo_link = editFields.photo_link.trim();
    if (!Object.keys(changed).length) { setEditError('Нет изменений'); setEditLoading(false); return; }
    try {
      const res = await manageAPI.updateSupplierItem(s.supplier_name, s.supplier_code, changed);
      setEditResult({ ...changed, job_id: res.data.job_id });
      // Оптимистично обновляем локальный стейт
      setItems(prev => prev.map(item => item.id === s.id ? {
        ...item,
        ...(changed.photo_url ? { supplier_photo_url: changed.photo_url } : {}),
        ...(changed.photo_link ? { photo_link: changed.photo_link } : {}),
      } : item));
    } catch (e) {
      setEditError(e.response?.data?.detail || 'Ошибка');
    } finally {
      setEditLoading(false);
    }
  };

  useEffect(() => {
    if (zoomIdx === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') { setZoomIdx(null); return; }
      if (e.key === 'ArrowLeft')  setZoomIdx(i => (i > 0 ? i - 1 : i));
      if (e.key === 'ArrowRight') setZoomIdx(i => (i < photoSuppliers.length - 1 ? i + 1 : i));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomIdx]);

  useEffect(() => {
    onZoomChange?.(zoomIdx !== null);
  }, [zoomIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    setSelectedSizes([]);
    Promise.allSettled([
      productsAPI.getSuppliers(productId),
      discountsAPI.list(),
    ]).then(([suppResult, discResult]) => {
      if (suppResult.status === 'fulfilled') {
        setItems(suppResult.value.data);
      }
      if (discResult.status === 'fulfilled') {
        const map = {};
        for (const d of discResult.value.data) map[d.supplier_name_norm] = d.discount_amount;
        setDiscountMap(map);
      }
    }).finally(() => setLoading(false));
  }, [productId]);

  // Все уникальные размеры по всем поставщикам, отсортированные
  const allSizes = [...new Set(
    items.flatMap(s => s.sizes ? s.sizes.split(' ').map(p => p.split(':')[0]) : [])
  )].sort((a, b) => parseFloat(a) - parseFloat(b));

  const toggleSize = (sz) =>
    setSelectedSizes(prev =>
      prev.includes(sz) ? prev.filter(s => s !== sz) : [...prev, sz]
    );

  // Сначала фильтруем по размерам, потом сортируем
  const afterFilter = selectedSizes.length === 0
    ? items
    : items.filter(s => {
        if (!s.sizes) return false;
        const itemSizes = s.sizes.split(' ').map(p => p.split(':')[0]);
        return selectedSizes.some(sz => itemSizes.includes(sz));
      });

  const _effectivePrice = (s) => {
    const disc = discountMap[stripEmoji(s.supplier_name || '')] ?? 0;
    return Math.max(0, (s.price_numeric || 0) - disc);
  };

  const displayed = [...afterFilter].sort((a, b) => {
    if (sortBy === 'price_asc')  return _effectivePrice(a) - _effectivePrice(b);
    if (sortBy === 'price_desc') return _effectivePrice(b) - _effectivePrice(a);
    if (sortBy === 'sizes_desc') {
      const countSizes = s => s.sizes ? s.sizes.split(' ').length : 0;
      return countSizes(b) - countSizes(a);
    }
    return 0;
  });

  // Таблица: количество для конкретного размера у поставщика, или null если
  // этого размера у него нет (такие строки идут в конец при сортировке
  // по колонке размера, независимо от направления).
  const qtyForSize = (s, size) => {
    if (!s.sizes) return null;
    const found = s.sizes.split(' ').find(p => p.split(':')[0] === size);
    return found ? found.split(':')[1] : null;
  };

  const tableSorted = [...afterFilter].sort((a, b) => {
    const { key, dir } = tableSort;
    const mul = dir === 'asc' ? 1 : -1;
    if (key === 'supplier') return mul * (a.supplier_name || '').localeCompare(b.supplier_name || '', 'ru');
    if (key === 'price') return mul * (_effectivePrice(a) - _effectivePrice(b));
    const qa = qtyForSize(a, key), qb = qtyForSize(b, key);
    const na = qa === null ? null : (parseInt(qa, 10) || 0);
    const nb = qb === null ? null : (parseInt(qb, 10) || 0);
    if (na === null && nb === null) return 0;
    if (na === null) return 1;
    if (nb === null) return -1;
    return mul * (na - nb);
  });

  if (loading) {
    return (
      <div className="suppliers-section">
        <div className="suppliers-loading">Загрузка наличия...</div>
      </div>
    );
  }

  if (items.length === 0) return null;

  const hasFilter = selectedSizes.length > 0;

  // Поставщики с фото для навигации в lightbox
  const photoSuppliers = displayed.filter(s => s.supplier_photo_url);
  const openZoom = (s) => {
    const idx = photoSuppliers.findIndex(x => x.id === s.id);
    if (idx >= 0) setZoomIdx(idx);
  };

  return (
    <div className="suppliers-section">
      <div className="suppliers-header">
        <span className="suppliers-title">
          Наличие у поставщиков
          <span className="suppliers-count">
            {hasFilter ? `${displayed.length} из ${items.length}` : items.length}
          </span>
        </span>
        <div className="suppliers-header-controls">
          {viewMode === 'cards' && (
            <button
              className={`suppliers-price-sort${sortBy !== 'default' ? ' suppliers-price-sort--active' : ''}`}
              onClick={() => setSortBy(cur => cyclePriceSort(cur))}
            >
              Цена&nbsp;
              {sortBy === 'price_asc' && (
                <svg width="11" height="11" viewBox="0 0 10 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle'}}>
                  <polyline points="2,9 5,4 8,9"/>
                </svg>
              )}
              {sortBy === 'price_desc' && (
                <svg width="11" height="11" viewBox="0 0 10 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle'}}>
                  <polyline points="2,5 5,10 8,5"/>
                </svg>
              )}
              {sortBy === 'default' && (
                <svg width="11" height="11" viewBox="0 0 10 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle'}}>
                  <polyline points="2,5 5,2 8,5"/>
                  <polyline points="2,9 5,12 8,9"/>
                </svg>
              )}
            </button>
          )}
          <label className="suppliers-view-toggle-wrap" title="Переключить вид: карточки / таблица">
            <span className="suppliers-view-toggle-label">{viewMode === 'table' ? 'Таблица' : 'Карточки'}</span>
            <span
              className={`suppliers-view-toggle${viewMode === 'table' ? ' suppliers-view-toggle--on' : ''}`}
              onClick={toggleViewMode}
              role="switch"
              aria-checked={viewMode === 'table'}
            >
              <span className="suppliers-view-toggle-knob" />
            </span>
          </label>
        </div>
      </div>

      {allSizes.length > 0 && (
        <div className="suppliers-size-filter">
          {hasFilter && (
            <button
              className="suppliers-size-reset"
              onClick={() => setSelectedSizes([])}
            >
              Все
            </button>
          )}
          {allSizes.map(sz => (
            <button
              key={sz}
              className={`size-chip${selectedSizes.includes(sz) ? ' active' : ''}`}
              onClick={() => toggleSize(sz)}
            >
              {sz}
            </button>
          ))}
        </div>
      )}

      {viewMode === 'table' ? (
      <div className="suppliers-table-wrap">
        <table className="suppliers-table">
          <thead>
            <tr>
              <th className="supplier-col" onClick={() => handleTableSort('supplier')}>
                Постав.{tableSort.key === 'supplier' && <span className="sort-arrow">{tableSort.dir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              <th onClick={() => handleTableSort('price')}>
                Цена{tableSort.key === 'price' && <span className="sort-arrow">{tableSort.dir === 'asc' ? '▲' : '▼'}</span>}
              </th>
              {allSizes.map(sz => (
                <th key={sz} onClick={() => handleTableSort(sz)}>
                  {sz}{tableSort.key === sz && <span className="sort-arrow">{tableSort.dir === 'asc' ? '▲' : '▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableSorted.length === 0 ? (
              <tr><td className="suppliers-empty" colSpan={allSizes.length + 2}>Нет поставщиков с выбранными размерами</td></tr>
            ) : tableSorted.map(s => {
              const discount = discountMap[stripEmoji(s.supplier_name || '')] ?? null;
              const discountedPrice = discount && s.price_numeric ? Math.max(0, s.price_numeric - discount) : null;
              return (
                <tr key={s.id} className="supplier-row" onClick={() => openZoom(s)}>
                  <td className="supplier-col">{s.supplier_name || '—'}</td>
                  <td className={`price-cell${discountedPrice ? ' price-cell--discounted' : ''}`}>
                    {discountedPrice ? discountedPrice.toLocaleString('ru-RU') : formatPrice(s.price)}
                  </td>
                  {allSizes.map(sz => {
                    const qty = qtyForSize(s, sz);
                    return <td key={sz} className={qty !== null ? 'qty-cell' : ''}>{qty ?? ''}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      ) : (
      <div className="suppliers-list">
          {displayed.length === 0 ? (
            <p className="suppliers-empty">Нет поставщиков с выбранными размерами</p>
          ) : displayed.map(s => {
            const sizes = s.sizes
              ? s.sizes.split(' ').map(p => {
                  const [sz, qty] = p.split(':');
                  return { sz, qty };
                })
              : [];

            const discount = discountMap[stripEmoji(s.supplier_name || '')] ?? null;
            const discountedPrice = discount && s.price_numeric
              ? Math.max(0, s.price_numeric - discount)
              : null;

            return (
              <div key={s.id} className={`supplier-card${discount ? ' supplier-card--discounted' : ''}`}>
                <div className="supplier-card-top">
                  {s.supplier_photo_url && (
                    <img
                      src={proxyImageUrl(withBust(s.supplier_photo_url))}
                      alt=""
                      className="supplier-photo supplier-photo--zoomable"
                      loading="lazy"
                      onClick={() => openZoom(s)}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="supplier-card-info">
                    <div className="supplier-header">
                      <span className="supplier-name">
                        {s.supplier_name || '—'}
                        {isAdmin && s.supplier_code && (
                          <span className="supplier-code-admin">
                            <span className="supplier-code-admin-text" title={s.supplier_code}>
                              {s.supplier_code}
                            </span>
                            <button
                              className="supplier-reassign-btn"
                              title="Изменить артикул каталога"
                              onClick={e => { e.stopPropagation(); editingId === s.id ? closeEdit() : openEdit(s); }}
                            >✎</button>
                          </span>
                        )}
                      </span>
                      <div className="supplier-header-right">
                        {discount && (
                          <span className="supplier-discount-badge">−{discount} ₴</span>
                        )}
                        {s.crm_link && (
                          <a
                            href={s.crm_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="supplier-crm-btn"
                          >
                            CRM
                          </a>
                        )}
                        {s.price && (
                          <span className="supplier-price">
                            {discountedPrice ? (
                              <>
                                <span className="supplier-price-original">{formatPrice(s.price)} ₴</span>
                                <span className="supplier-price-discounted">{discountedPrice.toLocaleString('ru-RU')} ₴</span>
                              </>
                            ) : (
                              <>{formatPrice(s.price)} ₴</>
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {sizes.length > 0 && (
                      <div className="supplier-sizes">
                        {sizes.map(({ sz, qty }) => (
                          <span key={sz} className="supplier-size-chip">
                            {sz}<span className="supplier-size-qty">×{qty}</span>
                          </span>
                        ))}
                      </div>
                    )}

                    {filterGrid(s.size_grid, s.sizes) && (
                      <div className="supplier-grid">{filterGrid(s.size_grid, s.sizes)}</div>
                    )}

                    <div className="supplier-links">
                      <SupplierLink href={s.photo_link}    label="Фото" />
                      <SupplierLink href={s.supplier_link} label="Поставщик" />
                      <SupplierLink href={s.table_link}    label="Таблица" />
                    </div>

                    {isAdmin && editingId === s.id && (
                      <div className="supplier-edit-form">
                        {editResult ? (
                          <div className="supplier-edit-success">
                            Задача #{editResult.job_id} в очереди:&nbsp;
                            {editResult.catalog_id && <span>артикул → <code>{editResult.catalog_id}</code></span>}
                            {editResult.photo_url && <span>фото</span>}
                            {editResult.photo_link && <span>TG</span>}
                            <button className="supplier-reassign-cancel" onClick={closeEdit}>✕</button>
                          </div>
                        ) : (
                          <>
                            <div className="supplier-edit-field">
                              <span className="supplier-edit-label">Артикул каталога</span>
                              <input
                                className="supplier-edit-input"
                                value={editFields.catalog_id}
                                onChange={e => setEditField('catalog_id', e.target.value)}
                                placeholder={productSku}
                                autoFocus
                                onKeyDown={e => e.key === 'Escape' && closeEdit()}
                              />
                            </div>
                            <div className="supplier-edit-field">
                              <span className="supplier-edit-label">Фото URL</span>
                              <div
                                style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                                onDragOver={e => e.preventDefault()}
                                onDrop={handlePhotoDrop}
                              >
                                <input
                                  className="supplier-edit-input"
                                  style={{ flex: 1 }}
                                  value={editFields.photo_url}
                                  onChange={e => setEditField('photo_url', e.target.value)}
                                  placeholder={photoUploading ? 'Загрузка…' : 'URL или перетяни / вставь фото'}
                                  onKeyDown={e => e.key === 'Escape' && closeEdit()}
                                  onPaste={handlePhotoPaste}
                                  readOnly={photoUploading}
                                />
                                <button type="button" className="supplier-reassign-save"
                                  style={{ whiteSpace: 'nowrap', padding: '4px 10px', fontSize: 12 }}
                                  disabled={photoUploading}
                                  onClick={() => photoUploadRef.current?.click()}>
                                  {photoUploading ? '…' : '📎'}
                                </button>
                              </div>
                            </div>
                            <div className="supplier-edit-field">
                              <span className="supplier-edit-label">TG ссылка</span>
                              <input
                                className="supplier-edit-input"
                                value={editFields.photo_link}
                                onChange={e => setEditField('photo_link', e.target.value)}
                                placeholder="https://t.me/..."
                                onKeyDown={e => e.key === 'Escape' && closeEdit()}
                              />
                            </div>
                            {editError && <div className="supplier-reassign-error">{editError}</div>}
                            <div className="supplier-edit-actions">
                              <button
                                className="supplier-reassign-save"
                                onClick={() => handleSaveEdit(s)}
                                disabled={editLoading}
                              >{editLoading ? '…' : 'Сохранить'}</button>
                              <button className="supplier-reassign-cancel" onClick={closeEdit}>Отмена</button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {zoomIdx !== null && (() => {
        const zs = photoSuppliers[zoomIdx];
        if (!zs) return null;
        const canPrev = zoomIdx > 0;
        const canNext = zoomIdx < photoSuppliers.length - 1;
        const zDiscount = discountMap[stripEmoji(zs.supplier_name || '')] ?? null;
        const zDiscountedPrice = zDiscount && zs.price_numeric
          ? Math.max(0, zs.price_numeric - zDiscount) : null;
        const zSizes = zs.sizes
          ? zs.sizes.split(' ').map(p => { const [sz, qty] = p.split(':'); return { sz, qty }; })
          : [];
        return ReactDOM.createPortal(
          <div
            className="photo-lightbox photo-lightbox--supplier"
            onClick={() => setZoomIdx(null)}
            onTouchStart={e => { lightboxTouchX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (lightboxTouchX.current === null) return;
              const dx = e.changedTouches[0].clientX - lightboxTouchX.current;
              lightboxTouchX.current = null;
              e.stopPropagation();
              if (dx > 50 && zoomIdx > 0) setZoomIdx(i => i - 1);
              else if (dx < -50 && zoomIdx < photoSuppliers.length - 1) setZoomIdx(i => i + 1);
            }}
          >
            <button className="lightbox-close" onClick={e => { e.stopPropagation(); setZoomIdx(null); }}>×</button>

            {canPrev && (
              <button className="lightbox-arrow lightbox-arrow--left"
                onClick={e => { e.stopPropagation(); setZoomIdx(i => i - 1); }}>‹</button>
            )}

            <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
              <img src={proxyImageUrl(withBust(zs.supplier_photo_url))} alt="" className="lightbox-photo" onError={e => { e.target.alt = '🖼 Фото недоступне'; e.target.style.minHeight='80px'; }} />
              {isAdmin && isOurImage(zs.supplier_photo_url) && (
                <div className="photo-rotate-bar">
                  <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                    onClick={e => { e.stopPropagation(); handleRotate(zs.supplier_photo_url, -90); }} title="Повернуть против часовой">↺</button>
                  <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                    onClick={e => { e.stopPropagation(); handleRotate(zs.supplier_photo_url, 90); }} title="Повернуть по часовой">↻</button>
                  <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                    onClick={e => { e.stopPropagation(); handleRevert(zs.supplier_photo_url); }} title="Восстановить оригинал">↩</button>
                </div>
              )}

              <div className="lightbox-info">
                <div className="lightbox-info-header">
                  <span className="lightbox-supplier-name">{zs.supplier_name}</span>
                  {zs.supplier_code && <span className="lightbox-supplier-code">№ {zs.supplier_code}</span>}
                  {product?.name && <span className="lightbox-product-name">{product.name}</span>}
                  <div className="lightbox-info-right">
                    {isAdmin && (
                      <button
                        className="supplier-reassign-btn lightbox-edit-btn"
                        title="Редактировать"
                        onClick={() => editingId === zs.id ? closeEdit() : openEdit(zs)}
                      >✎</button>
                    )}
                    {zs.crm_link && (
                      <a href={zs.crm_link} target="_blank" rel="noopener noreferrer" className="supplier-crm-btn">CRM</a>
                    )}
                    {zs.price && (
                      <span className="lightbox-price">
                        {zDiscountedPrice ? (
                          <>
                            <span className="supplier-price-original">{formatPrice(zs.price)} ₴</span>
                            <span className="supplier-price-discounted">{zDiscountedPrice.toLocaleString('ru-RU')} ₴</span>
                          </>
                        ) : <>{formatPrice(zs.price)} ₴</>}
                      </span>
                    )}
                  </div>
                </div>

                {zSizes.length > 0 && (
                  <div className="supplier-sizes lightbox-sizes">
                    {zSizes.map(({ sz, qty }) => (
                      <span key={sz} className="supplier-size-chip">{sz}<span className="supplier-size-qty">×{qty}</span></span>
                    ))}
                  </div>
                )}

                {filterGrid(zs.size_grid, zs.sizes) && (
                  <div className="supplier-grid lightbox-grid">{filterGrid(zs.size_grid, zs.sizes)}</div>
                )}

                <div className="supplier-links lightbox-links">
                  <SupplierLink href={zs.photo_link}    label="Фото" />
                  <SupplierLink href={zs.supplier_link} label="Поставщик" />
                  <SupplierLink href={zs.table_link}    label="Таблица" />
                </div>

                {isAdmin && editingId === zs.id && (
                  <div className="supplier-edit-form lightbox-edit-form">
                    {editResult ? (
                      <div className="supplier-edit-success">
                        Задача #{editResult.job_id} в очереди:&nbsp;
                        {editResult.catalog_id && <span>артикул → <code>{editResult.catalog_id}</code></span>}
                        {editResult.photo_url && <span>фото</span>}
                        {editResult.photo_link && <span>TG</span>}
                        <button className="supplier-reassign-cancel" onClick={closeEdit}>✕</button>
                      </div>
                    ) : (
                      <>
                        <div className="supplier-edit-field">
                          <span className="supplier-edit-label">Артикул каталога</span>
                          <input className="supplier-edit-input" value={editFields.catalog_id}
                            onChange={e => setEditField('catalog_id', e.target.value)}
                            placeholder={productSku} onKeyDown={e => e.key === 'Escape' && closeEdit()} />
                        </div>
                        <div className="supplier-edit-field">
                          <span className="supplier-edit-label">Фото URL</span>
                          <div
                            style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                            onDragOver={e => e.preventDefault()}
                            onDrop={handlePhotoDrop}
                          >
                            <input className="supplier-edit-input" style={{ flex: 1 }}
                              value={editFields.photo_url}
                              onChange={e => setEditField('photo_url', e.target.value)}
                              placeholder={photoUploading ? 'Загрузка…' : 'URL или перетяни / вставь фото'}
                              onKeyDown={e => e.key === 'Escape' && closeEdit()}
                              onPaste={handlePhotoPaste}
                              readOnly={photoUploading} />
                            <button type="button" className="supplier-reassign-save"
                              style={{ whiteSpace: 'nowrap', padding: '4px 10px', fontSize: 12 }}
                              disabled={photoUploading}
                              onClick={() => photoUploadRef.current?.click()}>
                              {photoUploading ? '…' : '📎'}
                            </button>
                          </div>
                        </div>
                        <div className="supplier-edit-field">
                          <span className="supplier-edit-label">TG ссылка</span>
                          <input className="supplier-edit-input" value={editFields.photo_link}
                            onChange={e => setEditField('photo_link', e.target.value)}
                            placeholder="https://t.me/..."
                            onKeyDown={e => e.key === 'Escape' && closeEdit()} />
                        </div>
                        {editError && <div className="supplier-edit-error">{editError}</div>}
                        <div className="supplier-edit-actions">
                          <button className="supplier-reassign-save" onClick={() => handleSaveEdit(zs)}
                            disabled={editLoading}>{editLoading ? '…' : 'Сохранить'}</button>
                          <button className="supplier-reassign-cancel" onClick={closeEdit}>Отмена</button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {photoSuppliers.length > 1 && (
                  <div className="lightbox-counter">{zoomIdx + 1} / {photoSuppliers.length}</div>
                )}
              </div>
            </div>

            {canNext && (
              <button className="lightbox-arrow lightbox-arrow--right"
                onClick={e => { e.stopPropagation(); setZoomIdx(i => i + 1); }}>›</button>
            )}
          </div>,
          document.body
        );
      })()}
      <input ref={photoUploadRef} type="file" accept="image/*"
        style={{ display: 'none' }} onChange={handlePhotoFileInput} />
    </div>
  );
};

const BellButton = ({ active, onClick }) => (
  <button
    className={`bell-btn${active ? ' bell-btn--active' : ''}`}
    onClick={onClick}
    title={active ? 'Управление подпиской' : 'Сообщить о поступлении'}
    style={{ background: active ? 'rgba(102,126,234,0.2)' : 'none', padding: '5px 6px' }}
  >
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
    </svg>
  </button>
);

const ShareButton = ({ sku }) => {
  const [state, setState] = useState('idle'); // idle | done | error
  const handle = () => {
    const url = `${window.location.origin}${window.location.pathname}?sku=${encodeURIComponent(sku)}`;
    navigator.clipboard.writeText(url)
      .then(() => { setState('done'); setTimeout(() => setState('idle'), 2000); })
      .catch(() => { setState('error'); setTimeout(() => setState('idle'), 2000); });
  };
  return (
    <button
      onClick={handle}
      title="Скопировать прямую ссылку"
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
        fontSize: 18, opacity: state === 'idle' ? 0.5 : 1,
        color: state === 'done' ? '#4ade80' : state === 'error' ? '#ff6b6b' : 'inherit',
        transition: 'opacity 0.15s, color 0.15s',
      }}
    >
      {state === 'done' ? '✓' : '🔗'}
    </button>
  );
};

const ProductModal = ({ product, onClose, onPrev, onNext, hasAlert, onAlert }) => {
  const [zoom, setZoom] = useState(false);
  const [dir, setDir] = useState(null);
  const isAdmin = useAuthStore((state) => state.user?.is_admin);
  const [addingToOrder, setAddingToOrder] = useState(false);
  const [orderAdded, setOrderAdded] = useState(false);
  const prevProductRef = useRef(product);
  const supplierZoomOpenRef = useRef(false);

  const [rotatingUrl, setRotatingUrl] = useState(null);
  const [photoTimestamps, setPhotoTimestamps] = useState(() => {
    try {
      const raw = localStorage.getItem('alldrop_photoTs');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return Object.fromEntries(Object.entries(parsed).filter(([, ts]) => ts > cutoff));
    } catch { return {}; }
  });
  const _OUR_ORIGIN_PM = 'https://alldrop.ink/images';
  const isOurImagePM = (url) => typeof url === 'string' && url.startsWith(_OUR_ORIGIN_PM);
  const withBustPM = (url) => {
    if (!url) return url;
    const base = url.split('?')[0];
    const ts = photoTimestamps[base];
    return ts ? `${base}?t=${ts}` : url;
  };
  const handleRotatePM = async (url, degrees) => {
    if (!url || rotatingUrl) return;
    const base = url.split('?')[0];
    setRotatingUrl(base);
    try {
      await adminItemsAPI.rotatePhoto(base, degrees);
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (err) {
      alert(`Ошибка поворота: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };
  const handleRevert = async (url) => {
    if (!url || rotatingUrl) return;
    const base = url.split('?')[0];
    setRotatingUrl(base);
    try {
      await adminItemsAPI.revertPhoto(base);
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (err) {
      alert(`Ошибка восстановления: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };

  // ── Замена главного фото товара каталога (только админ) ────────────────────
  const [photoReplaceFile,       setPhotoReplaceFile]       = useState(null); // файл, ожидающий кроп
  const [photoReplaceUploading,  setPhotoReplaceUploading]  = useState(false);
  const [localPhotoUrl,          setLocalPhotoUrl]          = useState(null); // локальный оверрайд после замены
  const [photoAreaDragOver,      setPhotoAreaDragOver]      = useState(false);
  const photoReplaceInputRef = useRef(null);
  const effectivePhotoUrl = localPhotoUrl || product.photo_url;

  // ── Редактирование названия товара каталога (только админ) ─────────────────
  const [editingName,   setEditingName]   = useState(false);
  const [nameValue,     setNameValue]     = useState(product.name);
  const [nameSaving,    setNameSaving]    = useState(false);
  const [localName,     setLocalName]     = useState(null); // локальный оверрайд после сохранения
  const effectiveName = localName || product.name;

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === effectiveName) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      await manageAPI.editCatalogProduct(product.sku, { name: trimmed });
      setLocalName(trimmed);
      setEditingName(false);
    } catch (err) {
      alert(`Ошибка сохранения названия: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setNameSaving(false);
    }
  };

  const handlePhotoReplaceFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // сброс — чтобы можно было выбрать тот же файл повторно
    if (file && file.type.startsWith('image/')) setPhotoReplaceFile(file);
  };

  const handlePhotoAreaDragOver = (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    setPhotoAreaDragOver(true);
  };
  const handlePhotoAreaDragLeave = (e) => {
    e.stopPropagation();
    setPhotoAreaDragOver(false);
  };
  const handlePhotoAreaDrop = (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    setPhotoAreaDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) setPhotoReplaceFile(file);
  };

  // Вставка з буфера обміну (Ctrl+V) — тільки коли фокус НЕ в текстовому полі
  // (щоб не заважати вставці, наприклад, у поле URL фото постачальника нижче).
  useEffect(() => {
    if (!isAdmin) return;
    const onPaste = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith('image/'));
      if (item) {
        const f = item.getAsFile();
        if (f) { e.preventDefault(); setPhotoReplaceFile(f); }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [isAdmin]);

  const handleCropConfirm = async (croppedFile) => {
    setPhotoReplaceFile(null);
    setPhotoReplaceUploading(true);
    try {
      const uploadRes = await adminItemsAPI.uploadPhoto(croppedFile);
      const newUrl = uploadRes.data.url;
      // Саму заміну (БД + КаталогТоваров + Setting/ALL) виконує фонова черга
      // задач — так само, як інші зміни товарів, з retry і видимістю у "Очередь".
      await manageAPI.replacePhotoJob(product.sku, newUrl);
      setLocalPhotoUrl(newUrl); // одразу показуємо нове фото, не чекаючи чергу
    } catch (err) {
      alert(`Ошибка замены фото: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setPhotoReplaceUploading(false);
    }
  };

  useEffect(() => {
    if (prevProductRef.current !== product) {
      prevProductRef.current = product;
      setLocalPhotoUrl(null);
      const timer = setTimeout(() => setDir(null), 200);
      return () => clearTimeout(timer);
    }
  }, [product]);

  const navigate = (fn, direction) => {
    if (!fn) return;
    setDir(direction);
    fn();
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { if (zoom) { setZoom(false); } else { onClose(); } return; }
      if (zoom) return;
      if (supplierZoomOpenRef.current) return; // lightbox поставщика перехватывает стрелки
      if (e.key === 'ArrowLeft')  navigate(onPrev, 'right');
      if (e.key === 'ArrowRight') navigate(onNext, 'left');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPrev, onNext, onClose, zoom]);

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    if (supplierZoomOpenRef.current) { touchStartX.current = null; touchStartY.current = null; return; }
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Перемикання товару лише якщо рух переважно горизонтальний — інакше
    // звичайний вертикальний скрол списку поставщиків (майже завжди з
    // невеликим нахилом убік) помилково перемикав на інший товар.
    if (Math.abs(dx) <= Math.abs(dy)) return;
    if (dx > SWIPE_THRESHOLD)  navigate(onPrev, 'right');
    if (dx < -SWIPE_THRESHOLD) navigate(onNext, 'left');
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="modal-wrapper" onClick={e => e.stopPropagation()}>

        <button
          className={`modal-nav modal-nav-prev${!onPrev ? ' modal-nav-disabled' : ''}`}
          onClick={() => navigate(onPrev, 'right')}
          disabled={!onPrev}
          title="Предыдущий (←)"
        >‹</button>

        <div className={`modal modal-navigable${dir ? ` modal-slide-${dir}` : ''}`}>
          <div className="modal-header">
            <div>
              {editingName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    className="modal-title-input"
                    value={nameValue}
                    autoFocus
                    disabled={nameSaving}
                    onChange={e => setNameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') { setNameValue(effectiveName); setEditingName(false); }
                    }}
                    style={{ font: 'inherit', fontWeight: 700, flex: 1, minWidth: 200 }}
                  />
                  <button className="photo-rotate-btn" disabled={nameSaving} onClick={handleSaveName} title="Сохранить (Enter)">
                    {nameSaving ? '…' : '✓'}
                  </button>
                  <button className="photo-rotate-btn" disabled={nameSaving}
                    onClick={() => { setNameValue(effectiveName); setEditingName(false); }} title="Отмена (Esc)">✕</button>
                </div>
              ) : (
                <h2 onClick={() => isAdmin && setEditingName(true)}
                    style={isAdmin ? { cursor: 'pointer' } : undefined}
                    title={isAdmin ? 'Клик — изменить название' : undefined}>
                  {effectiveName}{isAdmin && <span style={{ opacity: 0.35, marginLeft: 6, fontSize: '0.7em' }}>✏️</span>}
                </h2>
              )}
              {product.brand && product.model && (
                <p className="modal-subtitle">{product.brand} · {product.model}</p>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {onAlert && <BellButton active={hasAlert} onClick={onAlert} />}
              <ShareButton sku={product.sku} />
              <button className="close-btn" onClick={onClose}>×</button>
            </div>
          </div>

          <div className="modal-body">
            <input
              ref={photoReplaceInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoReplaceFile}
            />
            {effectivePhotoUrl ? (
              <div className="modal-image" onClick={() => setZoom(true)}
                   onDragOver={handlePhotoAreaDragOver}
                   onDragLeave={handlePhotoAreaDragLeave}
                   onDrop={handlePhotoAreaDrop}
                   style={photoAreaDragOver ? { outline: '2px dashed #2563eb', outlineOffset: -2 } : undefined}>
                <img src={withBustPM(effectivePhotoUrl)} alt={product.name} />
                <CopyButton
                  className="copy-btn--photo"
                  onCopy={() => copyImageToClipboard(effectivePhotoUrl)}
                />
                {isAdmin && (
                  <div className="photo-rotate-bar">
                    {isOurImagePM(effectivePhotoUrl) && (
                      <>
                        <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                          onClick={e => { e.stopPropagation(); handleRotatePM(effectivePhotoUrl, -90); }} title="Повернуть против часовой">↺</button>
                        <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                          onClick={e => { e.stopPropagation(); handleRotatePM(effectivePhotoUrl, 90); }} title="Повернуть по часовой">↻</button>
                        <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                          onClick={e => { e.stopPropagation(); handleRevert(effectivePhotoUrl); }} title="Восстановить оригинал">↩</button>
                      </>
                    )}
                    <button className="photo-rotate-btn" disabled={photoReplaceUploading}
                      onClick={e => { e.stopPropagation(); photoReplaceInputRef.current?.click(); }}
                      title="Замінити фото — клік, перетягніть файл або Ctrl+V">
                      {photoReplaceUploading ? '…' : '🖼'}
                    </button>
                  </div>
                )}
              </div>
            ) : isAdmin && (
              <div className="modal-image modal-image--empty"
                   onClick={() => photoReplaceInputRef.current?.click()}
                   onDragOver={handlePhotoAreaDragOver}
                   onDragLeave={handlePhotoAreaDragLeave}
                   onDrop={handlePhotoAreaDrop}
                   style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            minHeight: 160, borderRadius: 8,
                            border: `2px dashed ${photoAreaDragOver ? '#2563eb' : 'var(--border, #d1d5db)'}`,
                            background: photoAreaDragOver ? 'rgba(37,99,235,0.05)' : undefined }}>
                <span className="rt-hint" style={{ textAlign: 'center' }}>
                  {photoReplaceUploading ? 'Завантаження…' : <>🖼 Завантажити фото<br />
                    <span style={{ fontSize: 12 }}>клік, перетягніть файл або Ctrl+V</span></>}
                </span>
              </div>
            )}

            <div className="product-details">
              <div className="detail-row">
                <span className="detail-label">Артикул:</span>
                <SkuValue sku={product.sku} />
              </div>
              {product.season && (
                <div className="detail-row">
                  <span className="detail-label">Сезон:</span>
                  <span className="detail-value">{product.season}</span>
                </div>
              )}
            </div>

            <SuppliersSection
              productId={product.id}
              productSku={product.sku}
              product={product}
              onZoomChange={(open) => { supplierZoomOpenRef.current = open; }}
            />
          </div>

          <div className="modal-footer">
            {isAdmin && (
              <button
                className="btn btn-secondary"
                style={{ marginRight: 'auto', color: orderAdded ? '#4ade80' : undefined }}
                disabled={addingToOrder}
                onClick={async () => {
                  setAddingToOrder(true);
                  try {
                    await cheeseAPI.createOrder({
                      name: product.name,
                      photo_url: effectivePhotoUrl || null,
                      sizes_qty: {},
                      has_box: false,
                    });
                    setOrderAdded(true);
                    setTimeout(() => setOrderAdded(false), 2500);
                  } catch {
                    alert('Ошибка добавления в закупку');
                  } finally {
                    setAddingToOrder(false);
                  }
                }}
              >
                {orderAdded ? '✓ Добавлено' : addingToOrder ? '…' : 'В закупку'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={onClose}>Закрыть</button>
          </div>
        </div>

        <button
          className={`modal-nav modal-nav-next${!onNext ? ' modal-nav-disabled' : ''}`}
          onClick={() => navigate(onNext, 'left')}
          disabled={!onNext}
          title="Следующий (→)"
        >›</button>

      </div>

      {zoom && ReactDOM.createPortal(
        <div className="photo-lightbox" onClick={e => { e.stopPropagation(); setZoom(false); }}>
          <img
            src={withBustPM(effectivePhotoUrl)}
            alt={product.name}
            onClick={e => { e.stopPropagation(); setZoom(false); }}
          />
          <button className="lightbox-close" onClick={e => { e.stopPropagation(); setZoom(false); }}>×</button>
          {isAdmin && isOurImagePM(effectivePhotoUrl) && (
            <div className="photo-rotate-bar" style={{position:'fixed', bottom:60, left:'50%', transform:'translateX(-50%)', zIndex:2001}}>
              <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                onClick={e => { e.stopPropagation(); handleRotatePM(effectivePhotoUrl, -90); }} title="Повернуть против часовой">↺</button>
              <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                onClick={e => { e.stopPropagation(); handleRotatePM(effectivePhotoUrl, 90); }} title="Повернуть по часовой">↻</button>
              <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                onClick={e => { e.stopPropagation(); handleRevert(effectivePhotoUrl); }} title="Восстановить оригинал">↩</button>
            </div>
          )}
        </div>,
        document.body
      )}

      {photoReplaceFile && ReactDOM.createPortal(
        <CropModal
          file={photoReplaceFile}
          onConfirm={handleCropConfirm}
          onCancel={() => setPhotoReplaceFile(null)}
        />,
        document.body
      )}
    </div>
  );
};

export default ProductModal;
